<template>
  <div class="mobile-date-picker relative">
    <input
      ref="inputRef"
      :value="formattedDate"
      type="text"
      :placeholder="$t('common.datePicker.selectDate')"
      v-bind="requirementAttrs"
      readonly
      class="bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-level-800 dark:border-border-level-600 dark:placeholder:text-content-level-400 dark:text-content-inverse focus:outline-none focus:ring-2 focus:ring-primary h-[42px] pl-10 placeholder:text-content-level-500 pr-3 py-2 rounded-lg text-content-level-900 text-sm w-full"
      @click="openPicker"
    />

    <!-- Calendar Icon -->
    <CalendarIcon
      class="-translate-y-1/2 absolute h-4 left-3 text-content-level-400 top-1/2 transform w-4"
    />

    <!-- Date Picker Dropdown -->
    <div
      v-if="showPicker"
      class="absolute bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 date-picker-dropdown mt-1 rounded-lg shadow-lg z-(--z-index-ds-popup)"
      :style="pickerStyle"
    >
      <div class="border-b border-border-level-200 dark:border-border-level-700 p-3">
        <div class="flex items-center justify-between">
          <h3 class="dark:text-content-inverse font-semibold text-content-level-900 text-sm">
            {{ $t('common.datePicker.selectDate') }}
          </h3>

          <button
            type="button"
            class="hover:text-content-level-600 text-content-level-400"
            @click="closePicker"
          >
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>
      </div>

      <div class="p-3">
        <!-- Month/Year Navigation -->
        <div class="flex items-center justify-between mb-3">
          <button
            type="button"
            class="dark:hover:bg-surface-level-700 hover:bg-surface-level-100 p-1.5 rounded"
            @click="previousMonth"
          >
            <ChevronLeftIcon class="dark:text-content-level-400 h-4 text-content-level-600 w-4" />
          </button>

          <h4 class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
            {{ currentMonthYear }}
          </h4>

          <button
            type="button"
            class="dark:hover:bg-surface-level-700 hover:bg-surface-level-100 p-1.5 rounded"
            @click="nextMonth"
          >
            <ChevronRightIcon class="dark:text-content-level-400 h-4 text-content-level-600 w-4" />
          </button>
        </div>

        <!-- Calendar Grid -->
        <div
          class="dark:text-content-level-400 grid grid-cols-7 mb-1 text-center text-content-level-500 text-xs"
        >
          <div>
            {{ $t('common.datePicker.m') }}
          </div>

          <div>
            {{ $t('common.datePicker.t') }}
          </div>

          <div>
            {{ $t('common.datePicker.w') }}
          </div>

          <div>
            {{ $t('common.datePicker.t') }}
          </div>

          <div>
            {{ $t('common.datePicker.f') }}
          </div>

          <div>
            {{ $t('common.datePicker.s') }}
          </div>

          <div>
            {{ $t('common.datePicker.s') }}
          </div>
        </div>

        <div class="grid grid-cols-7 text-sm">
          <div
            v-for="day in calendarDays"
            :key="day.date"
            class="py-1"
          >
            <button
              type="button"
              v-bind="{
                'data-is-selected': day.isSelected ? '' : undefined,
                'data-is-current-month': day.isCurrentMonth ? '' : undefined,
                'data-is-today': day.isToday ? '' : undefined
              }"
              class="flex items-center justify-center mx-auto rounded-full size-7 transition-colors"
              :class="
                day.isPast
                  ? 'text-content-level-300 dark:text-content-level-600 cursor-not-allowed pointer-events-none'
                  : 'cursor-pointer not-data-is-selected:not-data-is-today:not-data-is-current-month:text-content-level-400 not-data-is-selected:hover:bg-surface-level-200 not-data-is-selected:not-data-is-today:data-is-current-month:text-content-level-900 data-is-selected:font-semibold data-is-selected:text-content-inverse data-is-selected:not-data-is-today:bg-primary data-is-today:font-semibold not-data-is-selected:data-is-today:text-primary data-is-selected:data-is-today:bg-primary dark:not-data-is-selected:not-data-is-today:not-data-is-current-month:!text-content-inverse dark:not-data-is-selected:hover:bg-surface-canvas/10 dark:not-data-is-selected:not-data-is-today:data-is-current-month:!text-content-inverse dark:data-is-selected:not-data-is-today:bg-primary dark:data-is-selected:not-data-is-today:!text-content-inverse dark:not-data-is-selected:data-is-today:!text-content-inverse dark:data-is-selected:data-is-today:bg-primary dark:data-is-selected:data-is-today:!text-content-inverse'
              "
              :data-date="day.date"
              @click="selectDateFromEvent"
            >
              <time :datetime="day.date">{{ day.date.split('-').pop()?.replace(/^0/, '') }}</time>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
  type InputHTMLAttributes
} from 'vue'
import {
  CalendarIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/vue/24/outline'
import { DateTime } from 'luxon'
import { getEffectiveTimezone } from '~/utils/getEffectiveTimezone'
import { useMonthCalendar } from '~/composables/useMonthCalendar'
import type { CalendarDay } from '~/interfaces/CalendarDay'

const props = withDefaults(
  defineProps<{
    /** Selected date in ISO format. */
    modelValue?: string
    /** Whether selecting a date is required. */
    required?: boolean
    /** Timezone used for date calculations. */
    timezone?: string | null
  }>(),
  { modelValue: '', required: false, timezone: null }
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

// State
const showPicker = ref(false)
const openPicker = () => {
  showPicker.value = true
}
const inputRef = useTemplateRef<HTMLInputElement>('inputRef')
const requirementAttrs = computed<Pick<InputHTMLAttributes, 'aria-required'>>(() => ({
  'aria-required': props.required
}))
const effectiveTimezone = computed(() => getEffectiveTimezone(props.timezone))

const { calendarDays, currentMonthYear, nextMonth, previousMonth, showMonthOf } = useMonthCalendar({
  isSelected: (date) => props.modelValue !== '' && props.modelValue === date,
  timezone: () => effectiveTimezone.value
})

// Computed properties
const formattedDate = computed(() => {
  if (!props.modelValue) return ''

  // Parse date string safely to avoid timezone issues
  const [year = 0, month = 1, day = 1] = props.modelValue.split('-').map(Number)
  const date = new Date(year, month - 1, day)

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
})

const pickerStyle = computed(() => {
  if (!showPicker.value || !inputRef.value) return {}

  return {
    maxWidth: '320px',
    minWidth: `${inputRef.value.offsetWidth}px`,
    width: 'auto'
  }
})

// Methods
const selectDate = (day: CalendarDay): void => {
  emit('update:modelValue', day.date)
  closePicker()
}

const selectDateFromEvent = (event: Event) => {
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  const date = calendarDays.value.find((day) => day.date === target.dataset['date'])
  if (date && !date.isPast) selectDate(date)
}

const closePicker = () => {
  showPicker.value = false
}

// Click outside handler
const handleClickOutside = (event: MouseEvent): void => {
  if (showPicker.value) {
    // Check if the click is on the input or dropdown
    const clickedElement = event.target
    const inputElement = inputRef.value
    const dropdownElement = document.querySelector('.date-picker-dropdown')

    // Check if click is on input, dropdown, or any of their children
    const isClickOnInput =
      inputElement !== null &&
      clickedElement instanceof Node &&
      (clickedElement === inputElement || inputElement.contains(clickedElement))
    const isClickOnDropdown =
      dropdownElement !== null &&
      clickedElement instanceof Node &&
      (clickedElement === dropdownElement || dropdownElement.contains(clickedElement))

    if (!isClickOnInput && !isClickOnDropdown) {
      closePicker()
    }
  }
}

// Watch for picker open/close
watch(showPicker, (isOpen) => {
  if (isOpen) {
    // Use nextTick to ensure DOM is updated
    nextTick(() => {
      document.addEventListener('click', handleClickOutside, true)
    }).catch(() => undefined)
    // Initialize to current month/year or selected date's month/year
    if (props.modelValue) {
      const selected = DateTime.fromISO(props.modelValue, { zone: effectiveTimezone.value })
      showMonthOf(selected.isValid ? selected.toJSDate() : new Date())
    } else {
      showMonthOf(new Date())
    }
  } else {
    document.removeEventListener('click', handleClickOutside, true)
  }
})

// Cleanup
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
